import React from 'react';
import Page from '../UI/Page';
import {
  Box,
  Container,
  Heading,
  Text,
  Divider,
  VStack,
  Card,
  CardBody,
  HStack,
  Icon,
  Badge,
  SimpleGrid,
} from "@chakra-ui/react";
import { StarIcon, TimeIcon } from "@chakra-ui/icons";

const About = () => {
  return (
    <Page>
      <div className="hero" style={{ minHeight: '70vh' }}>
        <Box py={12}>
          <Container maxW="900px" px={4}>
            <VStack spacing={8} align="stretch">
              
              {/* Header Section */}
              <VStack spacing={3} textAlign="center">
                <Heading
                  size={{ base: "lg", md: "xl" }}
                  color="teal.300"
                  fontWeight="extrabold"
                >
                  A Journey of Love, Legacy & Purpose
                </Heading>
                <Text fontSize="sm" color="gray.400" maxW="700px">
                  Every great story starts with a moment
                </Text>
              </VStack>

              {/* Main Story Card */}
              <Card
                bg="rgba(45, 55, 72, 0.7)"
                borderWidth="2px"
                borderColor="teal.700"
                backdropFilter="blur(10px)"
                boxShadow="0 0 30px rgba(56, 178, 172, 0.2)"
              >
                <CardBody p={6}>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={2} justify="center">
                      <Text fontSize="2xl">❤️</Text>
                      <Text fontSize="lg" color="white" fontWeight="semibold">
                        The Beginning
                      </Text>
                      <Text fontSize="2xl">❤️</Text>
                    </HStack>
                    
                    <Text fontSize="md" color="gray.300" lineHeight="tall" textAlign="center">
                      For me, that moment was the day I held my son for the first time –{' '}
                      <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
                        January 10, 2025
                      </Badge>
                      . In that instant, everything changed. This project was born from that overwhelming feeling of love and responsibility.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* Three Column Cards */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                
                {/* Mattias Card */}
                <Card
                  bg="rgba(45, 55, 72, 0.7)"
                  borderWidth="1px"
                  borderColor="purple.700"
                  backdropFilter="blur(10px)"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 0 25px rgba(159, 122, 234, 0.3)",
                    borderColor: "purple.500",
                  }}
                  transition="all 0.3s"
                >
                  <CardBody p={5}>
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={2} justify="center">
                        <Text fontSize="3xl">👶</Text>
                      </HStack>
                      <Heading size="md" color="white" textAlign="center">
                        Meet Mattias
                      </Heading>
                      <Divider borderColor="gray.700" />
                      <Text fontSize="sm" color="gray.300" lineHeight="tall">
                        Mattias is the heartbeat of this journey. From his very first breath, he became the reason I strive to build something meaningful—a foundation for his education, passions, and dreams.
                      </Text>
                      <Badge colorScheme="purple" textAlign="center" py={1}>
                        Born 2025
                      </Badge>
                    </VStack>
                  </CardBody>
                </Card>

                {/* About Me Card */}
                <Card
                  bg="rgba(45, 55, 72, 0.7)"
                  borderWidth="1px"
                  borderColor="cyan.700"
                  backdropFilter="blur(10px)"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 0 25px rgba(6, 182, 212, 0.3)",
                    borderColor: "cyan.500",
                  }}
                  transition="all 0.3s"
                >
                  <CardBody p={5}>
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={2} justify="center">
                        <Text fontSize="3xl">👨‍💻</Text>
                      </HStack>
                      <Heading size="md" color="white" textAlign="center">
                        About Me
                      </Heading>
                      <Divider borderColor="gray.700" />
                      <Text fontSize="sm" color="gray.300" lineHeight="tall">
                        I'm Angelin – a father, a tech enthusiast, and someone who believes in the power of smart investing and long-term thinking. This is my personal promise to prepare him for the future.
                      </Text>
                      <Badge colorScheme="cyan" textAlign="center" py={1}>
                        Dad & Builder
                      </Badge>
                    </VStack>
                  </CardBody>
                </Card>

                {/* About Project Card */}
                <Card
                  bg="rgba(45, 55, 72, 0.7)"
                  borderWidth="1px"
                  borderColor="green.700"
                  backdropFilter="blur(10px)"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "0 0 25px rgba(72, 187, 120, 0.3)",
                    borderColor: "green.500",
                  }}
                  transition="all 0.3s"
                >
                  <CardBody p={5}>
                    <VStack spacing={3} align="stretch">
                      <HStack spacing={2} justify="center">
                        <Text fontSize="3xl">📈</Text>
                      </HStack>
                      <Heading size="md" color="white" textAlign="center">
                        The Project
                      </Heading>
                      <Divider borderColor="gray.700" />
                      <Text fontSize="sm" color="gray.300" lineHeight="tall">
                        This isn't just a tracker—it's a living story of financial wisdom, milestones, and dreams. Built to show how small, consistent actions can grow into something meaningful.
                      </Text>
                      <Badge colorScheme="green" textAlign="center" py={1}>
                        Legacy Builder
                      </Badge>
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Mission Statement Card */}
              <Card
                bg="rgba(45, 55, 72, 0.7)"
                borderWidth="2px"
                borderColor="yellow.700"
                backdropFilter="blur(10px)"
                boxShadow="0 0 30px rgba(251, 191, 36, 0.2)"
              >
                <CardBody p={6}>
                  <VStack spacing={4}>
                    <HStack spacing={2}>
                      <Icon as={StarIcon} color="yellow.300" boxSize={5} />
                      <Heading size="md" color="white">
                        The Mission
                      </Heading>
                      <Icon as={StarIcon} color="yellow.300" boxSize={5} />
                    </HStack>
                    <Text fontSize="md" color="gray.300" lineHeight="tall" textAlign="center" maxW="700px">
                      By sharing this project publicly, I hope to inspire other parents and dreamers to start their own journey—to build not just wealth, but a lasting legacy. Every small investment, milestone, or lesson learned matters.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* Values Grid */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Card bg="rgba(45, 55, 72, 0.6)" borderWidth="1px" borderColor="gray.700">
                  <CardBody p={4} textAlign="center">
                    <Text fontSize="2xl" mb={2}>💡</Text>
                    <Text fontSize="sm" color="teal.200" fontWeight="bold">Transparency</Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      Open sharing of the journey
                    </Text>
                  </CardBody>
                </Card>

                <Card bg="rgba(45, 55, 72, 0.6)" borderWidth="1px" borderColor="gray.700">
                  <CardBody p={4} textAlign="center">
                    <Text fontSize="2xl" mb={2}>🎯</Text>
                    <Text fontSize="sm" color="teal.200" fontWeight="bold">Long-term Thinking</Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      Building for the future
                    </Text>
                  </CardBody>
                </Card>

                <Card bg="rgba(45, 55, 72, 0.6)" borderWidth="1px" borderColor="gray.700">
                  <CardBody p={4} textAlign="center">
                    <Text fontSize="2xl" mb={2}>❤️</Text>
                    <Text fontSize="sm" color="teal.200" fontWeight="bold">Love & Purpose</Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      Driven by family values
                    </Text>
                  </CardBody>
                </Card>
              </SimpleGrid>

              {/* Final Message Card */}
              <Card
                bg="rgba(45, 55, 72, 0.7)"
                borderWidth="2px"
                borderColor="teal.700"
                backdropFilter="blur(10px)"
              >
                <CardBody p={6} textAlign="center">
                  <VStack spacing={3}>
                    <Icon as={TimeIcon} color="teal.300" boxSize={6} />
                    <Heading size="md" color="white">
                      Because Every Step Counts...
                    </Heading>
                    <Text fontSize="sm" color="gray.300" lineHeight="tall" maxW="600px">
                      Whether it's a small investment, a big milestone, or simply a lesson learned—every moment matters. Let's build a future where our children have the freedom to chase their passions without limits.
                    </Text>
                    <Badge colorScheme="teal" fontSize="sm" px={4} py={2} mt={2}>
                      Started February 2025 🚀
                    </Badge>
                  </VStack>
                </CardBody>
              </Card>

            </VStack>
          </Container>
        </Box>
      </div>
    </Page>
  );
};

export default About;